<script>
    import Spinner from 'svelte-spinner';
    import { Tooltip } from "sveltestrap";
    import { createEventDispatcher } from 'svelte';
    import { Chart, registerables } from 'chart.js';
    
    import { vptServer, token } from '../../stores.js';
    import { getReferenceDataSurveyAnswerGroupingColour, getReferenceDataSurveyAnswerGroupingDescription } from "../../reference-data.js";
    import { formatDate, dateDescriptionStringAddExtraDescriptor } from '../../date-time.js';
    import { getSurveyOverdueStatus } from "../../survey.js";
    
    import SurveyModal from '../../modal/SurveyModal.svelte';
    
    const dispatch = createEventDispatcher();

    export let id;
    export let todaysConfig;
    export let selectedInmateAspect;

    let chart;
    Chart.register(...registerables);

    let surveyRecord;
    let scheduleDetails = null;

    let chartLabels = [];
    let chartDataset = [];

    let toastProperties = null; 

    $: {
        if (todaysConfig && todaysConfig.surveyUrn) {
            surveyRecord = null;
            getSurveyRecordAndRenderChart();
        }
    }

    async function getSurveyRecordAndRenderChart() {
        await fetch(`${$vptServer}/inmate/${id}/survey/overview`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            surveyRecord = data;
        })
        .catch(error => {
            console.error("Error getting inmate survey overview");
            console.error(error);
            return;
        });

        createChartData();
        renderSurveyChart();
    }

    function createChartData() {
        chartDataset = null;
        let datasetData = [];
        let datasetColors = [];

        if (surveyRecord.dateSurvey && surveyRecord.surveyAnswerGroups) {
            surveyRecord.surveyAnswerGroups.forEach((answer) => {
                chartLabels.push(getReferenceDataSurveyAnswerGroupingDescription(todaysConfig.surveyUrn.valueOf(), answer.grouping, true));
                datasetData.push(answer.total);
                datasetColors.push(getReferenceDataSurveyAnswerGroupingColour(todaysConfig.surveyUrn.valueOf(), answer.grouping));
            });
            chartDataset = [{data: datasetData, backgroundColor: datasetColors}];
        }

    }

    function renderSurveyChart() {
        if (chartDataset) {
            var ctx = document.getElementById("survey-chart-canvas").getContext("2d");

            chart = new Chart(ctx, {
                type: 'pie',
                data: {
                    labels: chartLabels,
                    datasets: chartDataset
                },
                options: {
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false }
                    }
                }
            });
        }
    }

    function showSurveyModal() {
        scheduleDetails = {
            headerText: 'Schedule Details',
            inmateUrn: id,
            inmateName: null,
            surveyScheduleUrn: surveyRecord.surveyScheduleUrn,
            surveyScheduleTotal: surveyRecord.surveyScheduleTotal,
            surveyScheduleFrequency: surveyRecord.surveyScheduleFrequency,
            surveyScheduleNextSurveyDate: surveyRecord.surveyScheduleNextSurveyDate,
            surveyScheduleNumberCompleted: surveyRecord.surveyScheduleNumberCompleted,
        };
    }

    function closeSurveyModal(event) {
        if (event.detail.scheduleType != null)
        {
            toastProperties = {
                title: (event.detail.scheduleType === 'add') ? "Survey Schedule Added" : (event.detail.scheduleType === 'update' ? 'Survey Schedule Updated' : 'Survey Schedule Cancelled'),
                content: generateToastMessageContent(event.detail.scheduleType, event.detail.surveyScheduleTotal),
                isOpen: true
            }
            
            dispatch('schedule', {toastProperties: toastProperties});
        }

        scheduleDetails = null;

        if (event.detail.refresh) {
            surveyRecord = null;
            getSurveyRecordAndRenderChart();
        }
    }

    function generateToastMessageContent(scheduleType, surveyScheduleTotal) {
        if (scheduleType === 'add') {
            if (surveyScheduleTotal === 1) return surveyScheduleTotal + " survey added";
            else return "Block of " + surveyScheduleTotal + " surveys added";   
        } else if (scheduleType === 'cancel') {
            if (surveyScheduleTotal === 1) return surveyScheduleTotal + " survey cancelled";
            else return "Block of " + surveyScheduleTotal + " surveys cancelled";
        } else if (scheduleType === 'update') {
            if (surveyScheduleTotal === 1) return surveyScheduleTotal + " survey updated";
            else return "Block of " + surveyScheduleTotal + " surveys updated";
        }
    }
</script>

<style>
    .inmate-widget {
        width: 270px;
    }

    .inmate-widget.widget-compact {
        width: 200px;
    }

    .widget-content {
        align-items: center;
    }

    .chart-label-widget {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .chart-label-widget .chart-widget {
        height: 75px;
        margin-bottom: 5px;
    }

    .badge-level {
        width: auto;
    }

    .list-widget-body {
        padding: 2px 0 0 0;
    }

    .list-widget-body .survey-detail {
        font-size: 12px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin: 0 0 3px 0;
    }

    .schedule-survey button {
        height: 26px;
        width: 20px;
        position: absolute;
        bottom: 5px;
        right: 5px;
        line-height: 16px;
        border: 1px transparent;
        border-radius: 5px;
        padding: 2px 3px 3px 3px;
        color: #0267B3;
        font-size: 14px;
        background-color: transparent;
    }

    .schedule-survey button:hover {
        background-color: #bad1e0;
    }

    .schedule-survey button .bi-stacked .bi-alert {
        top: -6px;
        font-size: 0.7em;
    }

    .inmate-widget .no-records {
	    padding: 0 10px 6px 10px;
    }

    .inmate-widget .no-records.no-records-found {
	    padding: 10px 10px 0 10px;
    }

    .inmate-widget .no-records.text-active {
        color: #0267B3;
    }
</style>

{#if todaysConfig && todaysConfig.surveyUrn}
    <div class="inmate-widget" class:widget-compact={surveyRecord && surveyRecord.totalCompleted == 0}>
        {#if surveyRecord}
            <button class="button-reset focus-outline card" on:click class:selected={selectedInmateAspect === todaysConfig.surveyLabelName}>
                <div class="card-body">
                    <div class="widget-container">
                        <span class="widget-label" class:widget-label-targeted={surveyRecord.indicatorTargeted}
                        class:widget-label-trend={surveyRecord.indicatorTrend == 'U' || surveyRecord.indicatorTrend == 'D'}>
                            {#if surveyRecord.dateSurvey && surveyRecord.indicatorTargeted}
                                <img id="targeted-indicator-survey" src="/assets/target-indicator.svg" alt="Targeted on the {todaysConfig.surveyLabelName} aspect" />
                                <Tooltip target="targeted-indicator-survey" placement="bottom">Targeted on the {todaysConfig.surveyLabelName} aspect</Tooltip>
                            {/if}
                            {todaysConfig.surveyLabelName}
                            {#if surveyRecord.dateSurvey && surveyRecord.indicatorTrend == 'U'}
                                <img id="trend-indicator-survey-increase" class="widget-trend-arrow" src="/assets/arrow/arrow-up-danger.svg" alt="Combined survey responses are more negative than their previous survey" />
                                <Tooltip target="trend-indicator-survey-increase" placement="bottom">Combined survey responses are more negative than their previous survey</Tooltip>
                            {:else if surveyRecord.dateSurvey && surveyRecord.indicatorTrend == 'D'}
                                <img id="trend-indicator-survey-decrease" class="widget-trend-arrow" src="/assets/arrow/arrow-down-success.svg" alt="Combined survey responses are more positive than their previous survey" />
                                <Tooltip target="trend-indicator-survey-decrease" placement="bottom">Combined survey responses are more positive than their previous survey</Tooltip>
                            {/if}
                        </span>

                        <div class="widget-content">
                            {#if surveyRecord.dateSurvey}
                                <div class="chart-label-widget">
                                    <div class="chart-widget">
                                        <canvas id="survey-chart-canvas"></canvas>
                                    </div>

                                    <div class="chart-label">
                                        {#if surveyRecord.level == 'H'}
                                            <span id="survey-widget-level-badge" class="badge badge-level badge-danger">High</span>
                                        {/if}
                                        {#if surveyRecord.level == 'M'}
                                            <span id="survey-widget-level-badge" class="badge badge-level badge-warning">Medium</span>
                                        {/if}
                                        {#if surveyRecord.level == 'L'}
                                            <span id="survey-widget-level-badge" class="badge badge-level badge-success">Low</span>
                                        {/if}
                                        <Tooltip target="survey-widget-level-badge" placement="bottom">
                                            Total Weighted Score: {surveyRecord.weightedPercentage}%
                                        </Tooltip>
                                    </div>
                                </div>
                            {/if}

                            <div class="list-widget">
                                <div class="list-widget-body">
                                    {#if !surveyRecord.dateSurvey}
                                        {#if surveyRecord.totalCompleted > 0}
                                            <span class="no-records">No {todaysConfig.surveyLabelName} surveys have been completed{todaysConfig.surveyPastDaysToAnalyse ? " in the last " + todaysConfig.surveyPastDaysToAnalyse + " days." : "."}</span>
                                            <span class="no-records">{surveyRecord.totalCompleted} {surveyRecord.totalCompleted != 1 ? 'surveys' : 'survey'} in total.</span>
                                        {:else}
                                            <span class="no-records no-records-found">No {todaysConfig.surveyLabelName} surveys have been completed.</span>
                                        {/if}
                                        <span class="no-records text-active" class:text-danger={surveyRecord.surveyScheduleUrn && getSurveyOverdueStatus(surveyRecord.surveyScheduleNextSurveyDate)}>
                                            {surveyRecord.surveyScheduleUrn ? 'Survey ' + (getSurveyOverdueStatus(surveyRecord.surveyScheduleNextSurveyDate) ? 'overdue ' + (dateDescriptionStringAddExtraDescriptor(surveyRecord.surveyScheduleNextSurveyDateDescription, false) === 'Yesterday' ? 'since ' : 'by ') : 'due ') + dateDescriptionStringAddExtraDescriptor(surveyRecord.surveyScheduleNextSurveyDateDescription, false).toLowerCase() + '.': ''}
                                        </span>
                                    {:else}
                                        {#if surveyRecord.surveyScheduleUrn}
                                            <span id="survey-detail-total-completed" class="survey-detail">
                                                <img id="survey-detail-total-completed-icon" src="/assets/check.svg" alt="Total Completed" />
                                                {surveyRecord.surveyScheduleNumberCompleted} of {surveyRecord.surveyScheduleTotal} Completed
                                            </span>

                                            {#if surveyRecord.surveyScheduleLatestSurveyDateDescription}
                                                <span id="survey-detail-last-completed" class="survey-detail">
                                                    <img id="survey-detail-last-completed-icon" src="/assets/arrow/chevron-left.svg" alt="Last Completed" />
                                                    {dateDescriptionStringAddExtraDescriptor(surveyRecord.surveyScheduleLatestSurveyDateDescription, true)}
                                                </span>
                                                <Tooltip target="survey-detail-last-completed" placement="bottom">Last completed on {formatDate(surveyRecord.surveyScheduleLatestSurveyDate)}</Tooltip>
                                            {/if}
                                            
                                            {#if surveyRecord.surveyScheduleNextSurveyDateDescription}
                                                <span id="survey-detail-next-due" class="survey-detail" class:text-danger={getSurveyOverdueStatus(surveyRecord.surveyScheduleNextSurveyDate)}>
                                                    <img id="survey-detail-next-due-icon" src="/assets/arrow/chevron-double-right{getSurveyOverdueStatus(surveyRecord.surveyScheduleNextSurveyDate) ? '-danger': ''}.svg" alt="Next Due Date"/>
                                                    {dateDescriptionStringAddExtraDescriptor(surveyRecord.surveyScheduleNextSurveyDateDescription, true)}
                                                </span>
                                                <Tooltip target="survey-detail-next-due" placement="bottom">Next due on {formatDate(surveyRecord.surveyScheduleNextSurveyDate)}</Tooltip>
                                            {/if}
                                        {:else}
                                            <span id="survey-detail-last-completed-date" class="survey-detail">
                                                <img id="survey-detail-last-completed-date-icon" src="/assets/arrow/chevron-left.svg" alt="Last Completed" />
                                                {dateDescriptionStringAddExtraDescriptor(surveyRecord.dateSurveyDescription, true)}
                                            </span>
                                            <Tooltip target="survey-detail-last-completed-date" placement="bottom">Last completed on {formatDate(surveyRecord.dateSurvey)}</Tooltip>
                                        {/if}
                                    {/if}
                                </div>
                            </div>
                        </div>

                        <div class="schedule-survey">
                            {#if surveyRecord.surveyScheduleUrn}
                                <button id="button-inmate-schedule-survey-update" type="button" class="btn" on:click|stopPropagation={showSurveyModal}>
                                    <span class="bi-stacked">
                                        <i class="bi bi-clipboard-data-fill" />
                                        {#if getSurveyOverdueStatus(surveyRecord.surveyScheduleNextSurveyDate)}
                                            <i class="bi bi-clock-fill bi-alert" />
                                        {/if}
                                    </span>
                                </button>
                                <Tooltip target="button-inmate-schedule-survey-update" placement="bottom">Update Survey Schedule</Tooltip>
                            {:else}
                                <button id="button-inmate-schedule-survey-add" type="button" class="btn" on:click|stopPropagation={showSurveyModal}>
                                    <span class="bi-stacked">
                                        <i class="bi bi-clipboard-data" />
                                    </span>
                                </button>
                                <Tooltip target="button-inmate-schedule-survey-add" placement="bottom">Add Survey Schedule</Tooltip>
                            {/if}
                        </div>
                    </div>
                </div>
            </button>
        {:else}
            <div class="card" class:selected={selectedInmateAspect === todaysConfig.surveyLabelName}>
                <div class="card-body">
                    <div class="widget-container">
                        <div class="widget-spinner">
                            <Spinner size="100" speed="750" color="#3d5980" thickness="3" gap="15" />
                        </div>
                    </div>
                </div>
            </div>
        {/if}
    </div>
    {#if scheduleDetails}
        <SurveyModal {...scheduleDetails} on:close={closeSurveyModal} />
    {/if}
{/if}